// fetchRawReads — the reads SupabaseStore.fetchAll and the public snapshot
// share. Prefers a single get_app_state() RPC (one connection, one round-trip)
// and falls back to 16 parallel per-table reads when that function isn't
// deployed yet (migration 79 not applied) so a deploy-before-migrate window
// degrades to the old working path instead of blanking the app.
//
// Returns the RawReads bundle for assembleState, OR a failure. The caller
// decides what a failure means: the store keeps its last-good state; the Worker
// returns 503 (and serves the stale cached snapshot).

import type { SupabaseClient } from '@supabase/supabase-js';
import type { RawReads } from './assembleState';

export type RawReadsResult =
  | { ok: true; raw: RawReads }
  | { ok: false; failed: string[] };

// Core tables that must read cleanly for a snapshot to be trustworthy (used by
// the fallback path; the RPC is all-or-nothing).
const CORE = ['matches', 'match_state', 'event_state', 'inspection_queue', 'teams'] as const;

const asArray = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

// Map the get_app_state() JSON (keys = RawReads fields, snake_case rows) → RawReads.
function mapRpc(data: Record<string, unknown>): RawReads {
  return {
    matches: asArray(data.matches),
    matchStates: asArray(data.matchStates),
    eventState: (data.eventState as RawReads['eventState']) ?? null,
    queue: asArray(data.queue),
    warnings: asArray(data.warnings),
    checkIn: asArray(data.checkIn),
    robots: asArray(data.robots),
    preInsp: asArray(data.preInsp),
    rounds: asArray(data.rounds),
    matchNotes: asArray(data.matchNotes),
    teamNotes: asArray(data.teamNotes),
    refereeModules: asArray(data.refereeModules),
    refereeModuleLog: asArray(data.refereeModuleLog),
    refereeOnLoan: asArray(data.refereeOnLoan),
    teams: asArray(data.teams),
    ecPenalties: asArray(data.ecPenalties),
    penalties3v3: asArray(data.penalties3v3),
    fieldCal: asArray(data.fieldCal),
    pictures: asArray(data.pictures),
    robotWeight: asArray(data.robotWeight),
  };
}

export async function fetchRawReads(supabase: SupabaseClient): Promise<RawReadsResult> {
  const { data, error } = await supabase.rpc('get_app_state');
  if (!error && data != null) {
    return { ok: true, raw: mapRpc(data as Record<string, unknown>) };
  }
  // The RPC is an optimization. On ANY failure — not deployed (PGRST202), an
  // RLS/policy error, a transient blip — fall back to the proven per-table
  // reads so the app keeps working. Log the cause so a persistent RPC failure
  // is diagnosable (otherwise it'd silently run the slower path forever).
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[fetchRawReads] get_app_state RPC failed; using per-table fallback:', error.message ?? error);
  }
  return fetchViaQueries(supabase);
}

// Legacy path: 16 parallel reads. Exported for tests and used as the fallback.
export async function fetchViaQueries(supabase: SupabaseClient): Promise<RawReadsResult> {
  const [
    matchesRes, matchStateRes, eventStateRes, queueRes, warningsRes, checkInRes,
    robotRes, preInspRes, roundsRes, matchNotesRes, teamNotesRes, teamsRes,
    ecPenaltiesRes, fieldCalRes, picturesRes, robotWeightRes,
    refModulesRes, refLogRes, refOnLoanRes, penalty3v3Res,
  ] = await Promise.all([
    supabase.from('matches')
      .select('id, kind, scheduled_at, red_team, blue_team, presenter, topic, field, is_bye')
      .order('scheduled_at'),
    supabase.from('match_state')
      .select('match_id, status_red, status_blue, winner_side'),
    supabase.from('event_state').select('*').eq('id', 'current').maybeSingle(),
    supabase.from('inspection_queue')
      .select('id, team_id, match_id, robot_class, position, busy_at_station, claimed_by, claimed_at, is_backup, pre_inspection, skipped, failed, urgent, s1_result, s2_result, s3_result, matches(kind, scheduled_at)')
      .order('position'),
    supabase.from('warnings')
      .select('*')
      .order('issued_at', { ascending: false }),
    supabase.from('check_in').select('*'),
    supabase.from('robot_inspection_station').select('team_id, robot_class, station, event, match_id, result, weight_kg, marked_at').is('completed_at', null),
    supabase.from('pre_inspection_level').select('team_id, event, robot_class, level'),
    supabase.from('match_rounds')
      .select('match_id, round_number, winner, red_score, blue_score')
      .order('round_number'),
    supabase.from('match_notes')
      .select('id, match_id, body, author_email, author_role, created_at')
      .order('created_at'),
    supabase.from('team_notes')
      .select('id, team_id, body, author_email, author_role, created_at')
      .order('created_at'),
    supabase.from('teams').select('id, group_3v3, initial_seed_1v1, practice_bonus'),
    supabase.from('ec_penalties').select('team_id, points_penalty_ec'),
    supabase.from('field_calibration').select('team_id, event, done'),
    supabase.from('team_pictures').select('team_id, event, robot_class, robot, driver, driver_name, driver_pronouns, chief, chief_name, chief_pronouns'),
    supabase.from('robot_weight').select('team_id, event, robot_class, instance, weight_kg, set_at'),
    supabase.from('referee_modules').select('team_id, module_type, ref_checkout, exchange_extras, ref_return'),
    supabase.from('referee_module_log')
      .select('id, team_id, body, author_email, author_role, created_at')
      .order('created_at'),
    // Public on-loan projection (view bypasses the ops-only table RLS, so anon
    // gets it for the team-page tab).
    supabase.from('referee_on_loan').select('team_id, module_type, on_loan'),
    // Public 3v3-penalty projection (view bypasses the ops-only check_in RLS,
    // so anon gets the RP penalty for the public standings).
    supabase.from('penalty_3v3').select('team_id, points_penalty_3v3'),
  ]);

  const coreErrors = [
    matchesRes.error, matchStateRes.error, eventStateRes.error, queueRes.error, teamsRes.error,
  ];
  const failed = CORE.filter((_, i) => coreErrors[i] != null);
  if (failed.length > 0) return { ok: false, failed };

  return {
    ok: true,
    raw: {
      matches: matchesRes.data ?? [],
      matchStates: matchStateRes.data ?? [],
      eventState: eventStateRes.data ?? null,
      queue: queueRes.data ?? [],
      warnings: warningsRes.data ?? [],
      checkIn: checkInRes.data ?? [],
      robots: robotRes.data ?? [],
      preInsp: preInspRes.data ?? [],
      rounds: roundsRes.data ?? [],
      matchNotes: matchNotesRes.data ?? [],
      teamNotes: teamNotesRes.data ?? [],
      refereeModules: refModulesRes.data ?? [],
      refereeModuleLog: refLogRes.data ?? [],
      refereeOnLoan: refOnLoanRes.data ?? [],
      teams: teamsRes.data ?? [],
      ecPenalties: ecPenaltiesRes.data ?? [],
      penalties3v3: penalty3v3Res.data ?? [],
      fieldCal: fieldCalRes.data ?? [],
      pictures: picturesRes.data ?? [],
      robotWeight: robotWeightRes.data ?? [],
    },
  };
}
